<template>
  <div class="flex flex-col h-full w-full">
    <!-- Top navbar: spans full width -->
    <AppNavbar
      :sidebar-open="sidebarOpen"
      :is-refreshing="isRefreshing"
      :update-available="updateAvailable"
      :is-tracking="timeTracking.isTracking.value"
      :elapsed="timeTracking.elapsed.value"
      :format-duration="timeTracking.formatDuration"
      :last-focus-task-id="lastFocusTaskId"
      :last-focus-task-title="lastFocusTaskTitle"
      :organize-mode="organizeMode"
      @toggle-sidebar="toggleSidebar"
      @stop-tracking="timeTracking.stopSession()"
      @back-to-work="backToWork"
      @archive-completed="archiveCompleted"
      @refresh-data="refreshData"
      @apply-update="applyUpdate"
      @toggle-organize="organizeMode = !organizeMode"
    />

    <!-- 3-panel area below navbar -->
    <div class="flex flex-1 overflow-hidden">
      <!-- Mobile sidebar overlay -->
      <div
        v-if="sidebarOpen && isMobile"
        class="fixed inset-0 z-20 bg-black/30"
        @click="setSidebarOpen(false)"
      />

      <!-- Sidebar -->
      <aside class="workbench-sidebar" :class="{ open: sidebarOpen }">
        <ProjectsSidebar
          :projects="projectsStore.projects.value"
          :current-project-id="projectsStore.currentProjectId.value"
          @select="projectsStore.selectProject"
          @create="handleCreateProject"
          @update="handleUpdateProject"
          @delete="handleDeleteProject"
        />
      </aside>

      <!-- Main task list: no outer scroll — TaskList owns bulk strip + list scroll -->
      <div class="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <TaskList
          :sections="sectionsStore.sections.value"
          :tasks-store="tasksStore"
          :projects="projectsStore.projects.value"
          :filter-project-id="projectsStore.currentProjectId.value"
          :organize-mode="organizeMode"
          @select-task="handleSelectTask"
          @create-task="handleCreateTask"
          @toggle-complete="tasksStore.toggleComplete"
          @toggle-collapse="sectionsStore.toggleCollapse"
          @reorder="handleReorder"
          @update-task="handleUpdateTask"
          @delete-task="handleDeleteTask"
          @move-section="handleMoveSection"
          @change-project="handleChangeProject"
          @rename-section="handleRenameSection"
          @move-section-up="handleMoveSectionUp"
          @move-section-down="handleMoveSectionDown"
          @delete-section="handleDeleteSection"
          @create-section="handleCreateSection"
          @bulk-move-section="handleBulkMoveSection"
          @bulk-change-project="handleBulkChangeProject"
          @bulk-complete="handleBulkComplete"
          @bulk-delete="handleBulkDelete"
          @exit-organize="organizeMode = false"
        />
      </div>

      <!-- Detail panel -->
      <aside v-if="tasksStore.selectedTask.value" class="workbench-detail bg-white dark:bg-gray-50">
        <TaskDetailPanel
          :task="tasksStore.selectedTask.value"
          :projects="projectsStore.projects.value"
          :sections="sectionsStore.sections.value"
          :time-tracking="timeTracking"
          @update="handleUpdateTask"
          @delete="handleDeleteTask"
          @close="tasksStore.selectTask(null)"
          @start-session="timeTracking.startSession"
          @stop-session="timeTracking.stopSession"
        />
      </aside>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useApi } from '../composables/useApi.js'
import { useProjectsStore } from '../composables/useProjectsStore.js'
import { useSectionsStore } from '../composables/useSectionsStore.js'
import { useTasksStore } from '../composables/useTasksStore.js'
import { useTimeTracking } from '../composables/useTimeTracking.js'
import AppNavbar from './layout/AppNavbar.vue'
import ProjectsSidebar from './layout/ProjectsSidebar.vue'
import TaskList from './tasks/TaskList.vue'
import TaskDetailPanel from './tasks/TaskDetailPanel.vue'

const props = defineProps({
  csrfToken: { type: String, default: '' },
  initialData: { type: Object, default: null }
})

const api = useApi(props.csrfToken)
const projectsStore = useProjectsStore(api)
const sectionsStore = useSectionsStore(api)
const tasksStore = useTasksStore(api)
const timeTracking = useTimeTracking(api)

const SIDEBAR_STORAGE_KEY = 'taskmagic-sidebar-open'

function readSidebarOpenPreference() {
  try {
    const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY)
    if (stored === 'true') return true
    if (stored === 'false') return false
  } catch {
    // ignore storage errors (private mode, etc.)
  }
  return window.innerWidth > 768
}

const sidebarOpen = ref(readSidebarOpenPreference())
const isMobile = ref(window.innerWidth <= 768)
const lastFocusTaskId = ref(null)
const isRefreshing = ref(false)
const updateAvailable = ref(false)
const organizeMode = ref(false)

function setSidebarOpen(open) {
  sidebarOpen.value = open
  try {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, String(open))
  } catch {
    // ignore storage errors
  }
}

function toggleSidebar() {
  setSidebarOpen(!sidebarOpen.value)
}

function hasInitialData() {
  return Boolean(
    props.initialData &&
    Array.isArray(props.initialData.projects) &&
    Array.isArray(props.initialData.sections) &&
    Array.isArray(props.initialData.tasks)
  )
}

function hydrateInitialState() {
  if (!hasInitialData()) return

  projectsStore.projects.value = props.initialData.projects
  sectionsStore.sections.value = props.initialData.sections
  tasksStore.tasks.value = props.initialData.tasks

  if (props.initialData.active_session) {
    lastFocusTaskId.value = props.initialData.active_session.task_id
    timeTracking.hydrateActiveSession(
      {
        id: props.initialData.active_session.id,
        started_at: props.initialData.active_session.started_at,
      },
      props.initialData.active_session.task_id
    )
  }
}

function refreshWorkbench() {
  return Promise.all([
    projectsStore.fetchProjects(),
    sectionsStore.fetchSections(),
    tasksStore.fetchTasks({ all: true }),
  ])
}

async function refreshData() {
  if (isRefreshing.value) return

  isRefreshing.value = true
  try {
    await refreshWorkbench()
    if (tasksStore.selectedTaskId.value) {
      await tasksStore.fetchTaskDetail(tasksStore.selectedTaskId.value)
    }
  } finally {
    isRefreshing.value = false
  }
}

function handleResize() {
  isMobile.value = window.innerWidth <= 768
}

function handleKeydown(e) {
  if (e.key === 'Escape') {
    if (tasksStore.selectedTask.value) {
      tasksStore.selectTask(null)
    } else if (sidebarOpen.value) {
      setSidebarOpen(false)
    }
  }
}

function handlePwaUpdateAvailable() {
  updateAvailable.value = true
}

async function syncPendingUpdateState() {
  if (!('serviceWorker' in navigator)) return

  const registration = await navigator.serviceWorker.getRegistration()
  updateAvailable.value = Boolean(registration?.waiting)
}

const lastFocusTaskTitle = computed(() => {
  if (!lastFocusTaskId.value) return ''
  const task = tasksStore.tasks.value.find(t => t.id === lastFocusTaskId.value)
  return task?.title || ''
})

onMounted(async () => {
  window.addEventListener('resize', handleResize)
  window.addEventListener('keydown', handleKeydown)
  window.addEventListener('taskmagic:pwa-update-available', handlePwaUpdateAvailable)
  syncPendingUpdateState().catch(() => {})

  hydrateInitialState()

  if (hasInitialData()) {
    const refresh = () => refreshWorkbench().catch(() => {})
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(refresh, { timeout: 1500 })
    } else {
      window.setTimeout(refresh, 250)
    }
    return
  }

  await refreshWorkbench()
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('taskmagic:pwa-update-available', handlePwaUpdateAvailable)
})

async function applyUpdate() {
  if (!('serviceWorker' in navigator)) {
    window.location.reload()
    return
  }

  const registration = await navigator.serviceWorker.getRegistration()
  if (registration?.waiting) {
    registration.waiting.postMessage({ type: 'SKIP_WAITING' })
    return
  }

  window.location.reload()
}

async function handleCreateProject(attrs) {
  await projectsStore.createProject(attrs)
}

async function handleUpdateProject(id, attrs) {
  await projectsStore.updateProject(id, attrs)
}

async function handleDeleteProject(id) {
  await projectsStore.deleteProject(id)
}

async function handleCreateTask(attrs) {
  await tasksStore.createTask(attrs)
}

async function handleSelectTask(id) {
  tasksStore.selectTask(id)
  if (id) {
    lastFocusTaskId.value = id
    await tasksStore.fetchTaskDetail(id)
  }
}

async function handleUpdateTask(id, attrs) {
  await tasksStore.updateTask(id, attrs)
}

async function handleDeleteTask(id) {
  await tasksStore.deleteTask(id)
}

async function handleReorder(updates) {
  await tasksStore.batchUpdateTasks(updates)
}

async function handleMoveSection(taskId, sectionId) {
  await tasksStore.updateTask(taskId, { section_id: sectionId })
}

async function handleChangeProject(taskId, projectId) {
  await tasksStore.updateTask(taskId, { project_id: projectId })
}

async function handleBulkMoveSection(taskIds, sectionId) {
  if (!taskIds?.length || sectionId == null) return
  const updates = taskIds.map(id => ({ id, section_id: sectionId }))
  await tasksStore.batchUpdateTasks(updates)
}

async function handleBulkChangeProject(taskIds, projectId) {
  if (!taskIds?.length) return
  const updates = taskIds.map(id => ({ id, project_id: projectId }))
  await tasksStore.batchUpdateTasks(updates)
}

async function handleBulkComplete(taskIds) {
  if (!taskIds?.length) return
  const completedAt = new Date().toISOString()
  const updates = taskIds.map(id => ({ id, completed_at: completedAt }))
  await tasksStore.batchUpdateTasks(updates)
}

async function handleBulkDelete(taskIds) {
  if (!taskIds?.length) return
  // Sequential so one failure doesn't leave partial local state unclear
  for (const id of taskIds) {
    await tasksStore.deleteTask(id)
  }
}

async function handleRenameSection(id, name) {
  await sectionsStore.updateSection(id, { name })
}

async function handleMoveSectionUp(id) {
  const sections = sectionsStore.sections.value
  const idx = sections.findIndex(s => s.id === id)
  if (idx <= 0) return
  const updates = [
    { id: sections[idx].id, display_order: sections[idx - 1].display_order },
    { id: sections[idx - 1].id, display_order: sections[idx].display_order },
  ]
  await sectionsStore.batchUpdateSections(updates)
}

async function handleMoveSectionDown(id) {
  const sections = sectionsStore.sections.value
  const idx = sections.findIndex(s => s.id === id)
  if (idx < 0 || idx >= sections.length - 1) return
  const updates = [
    { id: sections[idx].id, display_order: sections[idx + 1].display_order },
    { id: sections[idx + 1].id, display_order: sections[idx].display_order },
  ]
  await sectionsStore.batchUpdateSections(updates)
}

async function handleDeleteSection(id) {
  if (confirm('Delete this section? Tasks will move to Masterlist.')) {
    await sectionsStore.deleteSection(id)
    await tasksStore.fetchTasks({ all: true })
  }
}

async function handleCreateSection(name) {
  const maxOrder = Math.max(...sectionsStore.sections.value.map(s => s.display_order), 0)
  await sectionsStore.createSection({ name, display_order: maxOrder + 1 })
}

function backToWork() {
  if (lastFocusTaskId.value) {
    handleSelectTask(lastFocusTaskId.value)
  }
}

async function archiveCompleted() {
  const archiveSection = sectionsStore.sections.value.find(s => s.slug === 'archive')
  if (!archiveSection) return

  const completedTasks = tasksStore.tasks.value.filter(
    t => t.completed_at && t.section_id !== archiveSection.id
  )
  if (completedTasks.length === 0) return

  const updates = completedTasks.map(t => ({
    id: t.id,
    section_id: archiveSection.id,
  }))
  await tasksStore.batchUpdateTasks(updates)
}
</script>
